<template>
  <div class="message-page">
    <!-- 顶部栏 -->
    <div class="mp-header">
      <span class="mp-title">消息中心</span>
      <span class="mp-back" @click="$router.back()">返回</span>
    </div>

    <div class="mp-body">
      <!-- 左侧会话列表 -->
      <div class="mp-list">
        <div class="mp-list-title">会话列表</div>
        <div v-if="loading" class="mp-tip">加载中...</div>
        <div v-else-if="conversations.length === 0" class="mp-tip">暂无会话</div>
        <div v-for="item in conversations" :key="item.user_id" class="mp-item"
          :class="{ active: activeKey === String(item.user_id) }" @click="openConversation(item)">
          <img class="mp-avatar" :src="getAvatar(item)" alt="" />
          <div class="mp-item-main">
            <div class="mp-item-name">{{ getName(item) }}</div>
            <div class="mp-item-last">{{ getLastText(item) }}</div>
          </div>
          <div class="mp-item-right">
            <span class="mp-time">{{ getTime(item) }}</span>
          </div>
        </div>
      </div>

      <!-- 右侧聊天面板 -->
      <div class="mp-chat">
        <template v-if="activeConversation">
          <div class="mp-chat-header">{{ getName(activeConversation) }}</div>
          <div class="mp-msgs" ref="msgBox">
            <div v-for="(m, idx) in messages" :key="idx" class="mp-msg" :class="{ self: m.senderId === myId }">
              <img class="mp-msg-avatar" :src="m.senderAvatar || defaultAvatar" alt="" />
              <div class="mp-msg-content">
                <span v-if="m.isImage && m.mediaUrl">
                  <img class="mp-msg-img" :src="m.mediaUrl" alt="图片" />
                </span>
                <span v-else-if="m.isVideo && m.mediaUrl">
                  <video class="mp-msg-video" :src="m.mediaUrl" controls playsinline></video>
                </span>
                <span v-else>{{ m.text }}</span>
              </div>
            </div>
          </div>
          <div class="mp-input">
            <div class="mp-input-tools">
              <label class="mp-tool-btn" title="发送图片">
                <i class="el-icon-picture"></i>
                <input ref="imgInput" type="file" accept="image/*" hidden @change="onPickImage" />
              </label>
              <label class="mp-tool-btn" title="发送视频">
                <i class="el-icon-video-camera"></i>
                <input ref="videoInput" type="file" accept="video/*" hidden @change="onPickVideo" />
              </label>
            </div>
            <el-input type="textarea" :rows="3" v-model="draft" placeholder="输入消息，回车发送"
              @keyup.enter.native="sendText" />
            <el-button type="primary" size="small" @click="sendText">发送</el-button>
          </div>
        </template>
        <div v-else class="mp-chat-empty">请选择左侧会话开始聊天</div>
      </div>
    </div>
  </div>
</template>

<script>
import GoEasy from 'goeasy'
import { goEasy, ensureGoEasyConnected } from '@/utils/goeasy'
import { decrypt } from '@/utils/crypto.js'

// 默认头像（内联 SVG，避免依赖不存在的图片资源）
const defaultAvatar =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect width="80" height="80" fill="#d9d9d9"/><circle cx="40" cy="32" r="16" fill="#fff"/><path d="M16 72c0-13 11-22 24-22s24 9 24 22z" fill="#fff"/></svg>'
  )

export default {
  name: 'ConversationList',
  data() {
    return {
      defaultAvatar,
      loading: true,
      conversations: [],
      activeConversation: null,
      activeKey: '',
      messages: [],
      draft: '',
      myId: '',
      sendingMedia: false,
      targetUser: null,
    }
  },
  created() {
    const user = this.$store.getters.getUserInfo
    this.myId = String((user && (user.user_id || user.aid || user.id)) || '')
    // 进入聊天页确保 GoEasy 已连接（断线自动重连）
    if (user) ensureGoEasyConnected(user)
    // 从商品页/店铺页带入的目标用户（新建对话）
    const q = this.$route.query || {}
    this.targetUser = q.toUserId
      ? {
          user_id: String(q.toUserId),
          name: q.toName || '',
          avatar: q.toAvatar || '',
        }
      : null
    this.fetchConversations()
    // 监听新消息
    goEasy.im.on(GoEasy.IM_EVENT.PRIVATE_MESSAGE_RECEIVED, this.onMessageReceived)
  },
  beforeDestroy() {
    goEasy.im.off(GoEasy.IM_EVENT.PRIVATE_MESSAGE_RECEIVED, this.onMessageReceived)
  },
  methods: {
    fetchConversations() {
      this.loading = true
      // 若从商品/店铺页带入目标用户，查询时附加 to_user_id 入参
      const params = {}
      if (this.targetUser && this.targetUser.user_id) {
        params.to_user_id = String(this.targetUser.user_id)
      }
      this.$api.getChatUserList(params)
        .then((res) => {
          this.loading = false
          this.conversations = res || []

          // 若从商品/店铺页带入目标用户，自动打开对应会话（不存在则构造临时会话项）
          if (this.targetUser && !this.activeConversation) {
            const exist = this.conversations.find(
              (c) => String(c.user_id) === String(this.targetUser.user_id)
            )
            if (exist) {
              this.openConversation(exist)
            } else {
              this.openConversation({
                id: 'tmp_' + this.targetUser.user_id,
                user_id: this.targetUser.user_id,
                name: this.targetUser.name,
                avatar: this.targetUser.avatar,
              })
            }
          }
        })
        .catch((err) => {
          this.loading = false
          console.error('获取聊天用户列表失败:', err)
          this.$message.error('获取聊天用户列表失败')
        })
    },
    onMessageReceived(message) {
      // 收到新消息时，刷新左侧聊天用户列表（后端接口控制顺序与内容）
      this.fetchConversations()
      // 仅当处于对应会话时追加到消息列表
      if (!this.activeConversation) return
      const targetId = String(this.activeConversation.user_id)
      const fromId = String(message.senderId)
      const isCurrent = fromId === targetId || fromId === String(this.myId)
      if (!isCurrent) return
      this.appendMessage(message)
    },
    openConversation(item) {
      
      this.activeConversation = item
      this.activeKey = String(item.user_id)
      this.messages = []
      this.loadHistory(item)
      console.log(this.activeKey);

    },
    loadHistory(item) {
      // 走后端接口拉取历史聊天记录，入参 to_user_id 为对方用户id
      this.$api
        .getChatHistory({ to_user_id: String(item.user_id) })
        .then((res) => {
          // 接口返回字段说明：
          // user_id / user_id_2：对话双方；nickname / nickname_2 / avatar / avatar_2 与之对应
          // content：消息内容；create_time：发送时间
          const list = res || []
          this.messages = list
            .map((m) => {
              const isSelf = String(m.user_id) === String(this.myId)
              const avatar = isSelf ? m.avatar : m.avatar_2
              // 优先按 m.type 识别；若 type 为 text，再按 content 的 URL 后缀兜底识别图片/视频
              let rawType = (m.type || 'text').toString().toLowerCase()
              const content = m.content != null ? String(m.content) : ''
              let mediaUrl = ''
              if (rawType === 'text' && content) {
                // 兼容 JSON 包装 {t,c}
                const parsed = this.parsePayload(content)
                if (parsed.type && parsed.type !== 'text') {
                  rawType = parsed.type
                  mediaUrl = parsed.content || ''
                } else {
                  const lower = content.toLowerCase()
                  if (/\.(jpg|jpeg|png|gif|webp|bmp)(\?.*)?$/.test(lower)) {
                    rawType = 'image'
                    mediaUrl = content
                  } else if (/\.(mp4|webm|mov|m4v|avi|ogg)(\?.*)?$/.test(lower)) {
                    rawType = 'video'
                    mediaUrl = content
                  }
                }
              } else if (rawType === 'image' || rawType === 'img' || rawType === 'video') {
                mediaUrl = content || m.url || m.img || m.video || ''
              }
              const isImage = rawType === 'image' || rawType === 'img'
              const isVideo = rawType === 'video'
              return {
                senderId: String(m.user_id),
                senderAvatar: avatar || this.defaultAvatar,
                type: rawType,
                isImage,
                isVideo,
                text: !isImage && !isVideo ? content : '',
                mediaUrl,
              }
            })
          this.scrollToBottom()
        })
        .catch((err) => {
          console.error('获取历史消息失败:', err)
          this.$message.error('获取历史消息失败')
        })
    },
    sendText() {
      const text = (this.draft || '').trim()
      if (!text || !this.activeConversation) return
      const item = this.activeConversation
      const message = goEasy.im.createTextMessage({
        text: text,
        to: {
          type: 'private',
          id: String(item.user_id),
          data: {
            name: item.name,
            avatar: item.avatar,
          }
        }
      })
      goEasy.im.sendMessage({
        message: message,
        onSuccess: () => {
          this.draft = ''
          // 立即上屏（用本地方法，确保头像取自当前登录用户）
          this.appendSelfMessage(text, 'text')
          // 发送后刷新左侧会话列表（更新最后一条消息与时间）
          this.fetchConversations()
        },
        onFailed: (error) => {
          console.error('发送失败:', error)
          this.$message.error('发送失败')
        }
      })
    },
    // 选择图片
    async onPickImage(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      await this.sendMedia(file, 'image')
    },
    // 选择视频
    async onPickVideo(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      await this.sendMedia(file, 'video')
    },
    // 通过 GoEasy 原生 IM 发送图片/视频消息（SDK 自动上传并生成标准格式）
    async sendMedia(file, type) {
      if (!this.activeConversation) return
      if (this.sendingMedia) return
      this.sendingMedia = true
      try {
        const item = this.activeConversation
        const to = {
          type: 'private',
          id: String(item.user_id),
          data: {
            name: item.name,
            avatar: item.avatar,
          }
        }
        let message
        if (type === 'image') {
          message = goEasy.im.createImageMessage({ file, to })
        } else if (type === 'video') {
          message = goEasy.im.createVideoMessage({ file, to })
        } else {
          throw new Error('不支持的媒体类型')
        }
        goEasy.im.sendMessage({
          message,
          onSuccess: () => {
            // 构造与原生 payload 一致的数据用于本地上屏
            const payload = message.payload || {}
            const url = payload.url || (payload.video && payload.video.url) || ''
            this.appendSelfMessage(url, type)
            this.fetchConversations()
          },
          onProgress: (progress) => {
            console.log('上传进度:', progress)
          },
          onFailed: (err) => {
            console.error('发送失败:', err)
            this.$message.error('发送失败')
          }
        })
      } catch (err) {
        console.error('发送媒体失败:', err)
        this.$message.error(err.message || '发送失败')
      } finally {
        this.sendingMedia = false
      }
    },
    // 解析消息体：兼容 JSON 包装 {t,c} 与纯文本
    parsePayload(text) {
      if (!text) return { type: 'text', content: '' }
      try {
        const obj = JSON.parse(text)
        if (obj && typeof obj === 'object' && 't' in obj) {
          return { type: obj.t, content: obj.c }
        }
      } catch (e) {
        // 非 JSON，按纯文本处理
      }
      return { type: 'text', content: text }
    },
    appendMessage(message) {
      const payload = message.payload || {}
      const messageType = (message.messageType || '').toString().toLowerCase()
      let rawType = messageType
      let mediaUrl = ''
      let text = ''

      if (rawType === 'image' || payload.contentType || payload.url) {
        rawType = 'image'
        mediaUrl = payload.url || ''
      } else if (rawType === 'video' || (payload.video && payload.video.url)) {
        rawType = 'video'
        mediaUrl = payload.video && payload.video.url ? payload.video.url : ''
      } else {
        // 兼容旧版 JSON 包装 / 纯 URL 文本
        const raw = payload.text || ''
        const parsed = this.parsePayload(raw)
        rawType = parsed.type || 'text'
        const cnt = parsed.content || raw
        if (rawType === 'text' && cnt) {
          const lower = cnt.toLowerCase()
          if (/\.(jpg|jpeg|png|gif|webp|bmp)(\?.*)?$/.test(lower)) {
            rawType = 'image'
            mediaUrl = cnt
          } else if (/\.(mp4|webm|mov|m4v|avi|ogg)(\?.*)?$/.test(lower)) {
            rawType = 'video'
            mediaUrl = cnt
          } else {
            text = cnt
          }
        } else {
          mediaUrl = (rawType === 'image' || rawType === 'video') ? cnt : ''
          text = (rawType === 'image' || rawType === 'video') ? '' : cnt
        }
      }

      const isImage = rawType === 'image' || rawType === 'img'
      const isVideo = rawType === 'video'
      this.messages.push({
        senderId: message.senderId,
        senderAvatar: (message.senderData && message.senderData.avatar) || this.defaultAvatar,
        type: rawType,
        isImage,
        isVideo,
        text: text,
        mediaUrl: mediaUrl,
      })
      this.scrollToBottom()
    },
    // 在发送成功回调里，把"我"发出的消息上屏（使用当前登录用户头像）
    appendSelfMessage(text, type) {
      const user = this.$store.getters.getUserInfo || {}
      const rawType = (type || 'text').toString().toLowerCase()
      const isImage = rawType === 'image' || rawType === 'img'
      const isVideo = rawType === 'video'
      this.messages.push({
        senderId: String(this.myId),
        senderAvatar: user.avatar || this.defaultAvatar,
        type: rawType,
        isImage,
        isVideo,
        text: !isImage && !isVideo ? (text != null ? String(text) : '') : '',
        mediaUrl: isImage || isVideo ? text : '',
      })
      this.scrollToBottom()
    },
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.msgBox) {
          this.$refs.msgBox.scrollTop = this.$refs.msgBox.scrollHeight
        }
      })
    },
    getAvatar(item) {
      return item.avatar || this.defaultAvatar
    },
    getName(item) {
      return item.name || '私聊'
    },
    getLastText(item) {
      return item.content || ''
    },
    getTime(item) {
      return item.create_time || ''
    },
  },
}
</script>

<style scoped>
.message-page {
  max-width: 1100px;
  margin: 20px auto;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.mp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #eee;
}

.mp-title {
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50;
}

.mp-back {
  color: #286ced;
  cursor: pointer;
  font-size: 14px;
}

.mp-body {
  display: flex;
  height: 600px;
}

.mp-list {
  width: 300px;
  border-right: 1px solid #eee;
  overflow-y: auto;
}

.mp-list-title {
  padding: 12px 16px;
  font-weight: bold;
  color: #333;
  border-bottom: 1px solid #f2f2f2;
}

.mp-tip {
  padding: 30px 16px;
  color: #999;
  text-align: center;
  font-size: 14px;
}

.mp-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f7f7f7;
  background: #fff;
  transition: background 0.2s;
}

.mp-item:hover {
  background: #f5f8ff;
}

.mp-item.active {
  background: #eef4ff;
}

.mp-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 12px;
}

.mp-item-main {
  flex: 1;
  min-width: 0;
}

.mp-item-name {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.mp-item-last {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mp-item-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: 8px;
}

.mp-badge {
  background: #f56c6c;
  color: #fff;
  border-radius: 10px;
  padding: 0 6px;
  font-size: 12px;
  line-height: 18px;
  min-width: 18px;
  text-align: center;
}

.mp-time {
  font-size: 11px;
  color: #bbb;
  margin-top: 4px;
}

.mp-chat {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.mp-chat-header {
  padding: 14px 20px;
  border-bottom: 1px solid #eee;
  font-weight: bold;
  color: #333;
}

.mp-msgs {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  background: #fafafa;
}

.mp-msg {
  display: flex;
  margin-bottom: 14px;
}

.mp-msg.self {
  flex-direction: row-reverse;
}

.mp-msg-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
}

.mp-msg-content {
  max-width: 60%;
  margin: 0 10px;
  padding: 8px 12px;
  background: #fff;
  border-radius: 6px;
  font-size: 14px;
  color: #333;
  word-break: break-all;
}

.mp-msg-img {
  display: block;
  max-width: 200px;
  max-height: 200px;
  border-radius: 4px;
  cursor: pointer;
}

.mp-msg-video {
  display: block;
  max-width: 240px;
  border-radius: 4px;
  background: #000;
}

.mp-msg.self .mp-msg-content {
  background: #286ced;
  color: #fff;
}

.mp-input {
  border-top: 1px solid #eee;
  padding: 12px 16px;
  display: flex;
  gap: 10px;
  align-items: flex-end;
}

.mp-input-tools {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.mp-tool-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: #f5f7fa;
  color: #606266;
  cursor: pointer;
  font-size: 18px;
}

.mp-tool-btn:hover {
  background: #e6eefc;
  color: #286ced;
}

.mp-input .el-button {
  flex-shrink: 0;
}

.mp-chat-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bbb;
  font-size: 14px;
}
</style>
